import { MissionState } from "@shared/types";
import { css, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

/** CSS affecting the {@link MissionCanvas} */
const styles: string = css`
    :host {
        display: block;
        width: 320px;
        height: 100px;
        position: absolute;
        top: 0;
        right: 0;
    }

    .mission-container {
        padding: 10px;
    }

    .mission {
        background-color: rgb(139, 71, 71);
        border: 5px solid #440b0b4e;
        border-radius: 5px;
        margin-bottom: 10px;
        display: flex;
        flex-direction: column;
        padding: 10px 10px 5px 10px;
    }

    .mission h2 {
        font-size: 1.2em;
        margin: 0;
        line-height: 1.3rem;
        margin-bottom: 10px;
    }

    .item-container {
        display: flex;
        gap: 5px;
        align-items: center;
    }

    .item-container p {
        margin: 0;
        white-space: nowrap;
    }

    .item-container p#completed {
        color: rgb(0, 255, 0);
    }

    .item-container img {
        width: 22px;
        height: 22px;
        object-fit: contain;
    }
    .description-container p {
        margin: 0;
        line-height: 1.3rem;
        font-size: 0.8em;
        color: #909090;
    }
`;

/**
 * Represents the Mission on the page
 */
export class MissionComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current mission state */
    private _currentMissionState: MissionState | undefined;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshMissionState();
    }

    /**
     * Refresh the current mission state
     */
    private async refreshMissionState(): Promise<void> {
        const missionState: MissionState = await this._gameRouteService.getMissionState();
        this._currentMissionState = missionState;
        // Render and update the mission component
        this.render();
    }

    /**
     * Render the mission component
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }
        const element: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>
            
            ${this.renderSpecificMissions()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...element);
    }

    /**
     * Render the specific missions
     */
    private renderSpecificMissions(): HTMLElement {
        const missionContainer: HTMLElement = document.createElement("div");
        missionContainer.classList.add("mission-container");
        for (const mission of this._currentMissionState?.currentMissions ?? []) {
            if (mission === "rajan") {
                const cocnutMissionText: string = this.renderItem(
                    this._currentMissionState?.inventory.filter(item => item === "coconut").length ?? 0,
                    1,
                    "Coconut"
                );
                const fishMissionText: string = this.renderItem(
                    this._currentMissionState?.inventory.filter(item => item === "fish").length ?? 0,
                    1,
                    "Fish"
                );
                missionContainer.innerHTML += `
                    <div class="mission">
                        <h2>Find food for ${this._currentMissionState?.knownName}</h2>
                        <div class="item-container">
                            ${cocnutMissionText}<img src="/assets/img/item-icons/coconut.png" alt"coconut-item">
                        </div>
                        <div class="item-container">
                            ${fishMissionText}<img src="/assets/img/item-icons/fish.png" alt"fish-item">
                        </div>
                    </div>
                    `;
            }
            if (mission === "boat") {
                const woodMissionText: string = this.renderItem(
                    this._currentMissionState?.amountOfWoodItems ?? 0,
                    6,
                    "Wood"
                );
                missionContainer.innerHTML += `
                    <div class="mission">
                        <h2>Repair the Boat</h2>
                        <div class="item-container">
                            ${woodMissionText}<img src="/assets/img/item-icons/wood.png" alt"wood-item">
                        </div>
                        <div class="description-container">
                            <p>Drop the pieces of wood at the boat wreck</p>
                        </div> 
                    </div>
                    `;
            }
        }
        return missionContainer;
    }

    /**
     * Render an item for the mission
     *
     * @param itemsInInventory amount of items of a specific type of item in the inventory
     * @param itemsNeeded amount of items needed for the mission
     * @param itemName name used for text display
     *
     * @returns HTML string of the item
     */
    private renderItem(itemsInInventory: number, itemsNeeded: number, itemName: string): string {
        if (itemsInInventory >= itemsNeeded) {
            return `<p id="completed">${itemsInInventory}/${itemsNeeded} ${itemName}</p>`;
        }
        return `<p>${itemsInInventory}/${itemsNeeded} ${itemName}</p>`;
    }
}
